/**
 * Application Routes Configuration
 * ============================================================================
 *
 * Purpose:
 * --------
 * Centralized definition of all application routes.
 * Pure data structure separating route configuration from component rendering.
 *
 * This file defines:
 * - All application routes organized by feature
 * - Route permissions and layouts
 * - Navigation metadata
 * - Error and fallback routes
 *
 * Design Principles:
 * ------------------
 * - Declarative: Routes defined as data, not JSX
 * - Lazy loading: Components loaded on-demand
 * - Type-safe: Full TypeScript coverage
 * - Centralized: Single source of truth for routing
 * - Organized: Grouped by feature for maintainability
 *
 * Non-goals:
 * ----------
 * - Component implementation (defined in feature folders)
 * - Route rendering (handled by RouteRenderer)
 * - Permission checking (handled by ProtectedRoute)
 *
 * Usage:
 * ------
 * This configuration is consumed by RouteRenderer component:
 * ```typescript
 * <RouteRenderer config={appRouteConfig} />
 * ```
 *
 * Migration Notes:
 * ----------------
 * This replaces the old routes.config.js with:
 * - Better type safety
 * - Clearer structure
 * - Easier to maintain
 * - Supports lazy loading out of the box
 * ============================================================================
 */

import { errorPageRoutes, notFoundFallbackRoute } from './error-page-routes';

import type { AppRouteConfig } from 'navigation/types';
import { adminRoutes } from './admin-routes';
import { authRoutes } from './auth-routes';
import { claimRoutes } from './claim-routes';
import { customerRoutes } from './customer-routes';
import { agentRoutes } from './agent-routes';
import { branchRoutes } from './branch-routes';
import { complianceRoutes } from './compliance-routes';
import { reconciliationRoutes } from './reconciliation-routes';
import { relationshipsRoutes } from './relationships-routes';
import { paymentRoutes } from './payment-routes';
import { dashboardRoutes } from './overview-routes';
import { publicRoutes } from './public-routes';
import { coverNoteRoutes } from './cover-note-routes';
import { policyRoutes } from './policy-routes';
import { productRoutes } from './product-routes';
import { settingsRoutes } from './settings-routes';
import { userRoutes } from './user-routes';

/**
 * Complete Application Route Configuration
 * ============================================================================
 */
export const appRouteConfig: AppRouteConfig = {
  groups: [
    publicRoutes,
    authRoutes,
    adminRoutes,
    dashboardRoutes,
    coverNoteRoutes,
    policyRoutes,
    claimRoutes,
    customerRoutes,
    agentRoutes,
    relationshipsRoutes,
    complianceRoutes,
    reconciliationRoutes,
    branchRoutes,
    paymentRoutes,
    productRoutes,
    userRoutes,
    settingsRoutes,
    errorPageRoutes,
  ],
  fallback: notFoundFallbackRoute,
  errorPages: errorPageRoutes.routes,
};
